import '../../../styles/rte.css';

import { Card } from '@mantine/core';
import { randomId, useDisclosure } from '@mantine/hooks';
import {
    Link,
    RichTextEditor,
    type RichTextEditorToolbarProps,
} from '@mantine/tiptap';
import { Color } from '@tiptap/extension-color';
import Highlight from '@tiptap/extension-highlight';
import Placeholder from '@tiptap/extension-placeholder';
import SubScript from '@tiptap/extension-subscript';
import Superscript from '@tiptap/extension-superscript';
import TextAlign from '@tiptap/extension-text-align';
import { TextStyle } from '@tiptap/extension-text-style';
import { useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { useEffect, useState } from 'react';

export type Controlled_RichTextProps = {
    value: string;
    onChange?: (value: string) => void;
    readonly: boolean;
    toolbarProps?: RichTextEditorToolbarProps;
    toolBarOnFocus?: boolean;
    maxContentHeight?: number;
};

export const Controlled_RichText = ({
    value,
    onChange,
    readonly,
    toolbarProps,
    toolBarOnFocus,
    maxContentHeight,
}: Controlled_RichTextProps) => {
    const [content, setContent] = useState(value);
    const [updateKey, setUpdateKey] = useState('updateKey');

    useEffect(() => {
        if (content !== value) {
            setContent(value);
            setUpdateKey(randomId());
        }
    }, [content, value]);

    return (
        <InternalControlled_RichText
            key={updateKey}
            value={content}
            readonly={readonly}
            onChange={(newValue) => {
                setContent(newValue);
                if (onChange) {
                    onChange(newValue);
                }
            }}
            toolbarProps={toolbarProps}
            toolBarOnFocus={toolBarOnFocus}
            maxContentHeight={maxContentHeight}
        />
    );
};

const InternalControlled_RichText = ({
    value,
    onChange,
    readonly,
    toolbarProps,
    toolBarOnFocus,
    maxContentHeight,
}: Controlled_RichTextProps) => {
    const [focused, { close, open }] = useDisclosure(false);
    const editor = useEditor({
        extensions: [
            StarterKit.configure({ link: false }),
            Link,
            TextAlign.configure({ types: ['heading', 'paragraph'] }),
            TextStyle,
            Color,
            Superscript,
            SubScript,
            Highlight,
            Placeholder.configure({ placeholder: 'not defined' }),
        ],
        content: value,
        onUpdate({ editor }) {
            if (onChange) {
                onChange(editor.getHTML());
                editor.chain().focus();
            }
        },
        editable: !readonly,
        shouldRerenderOnTransaction: true,
    });

    if (readonly) {
        return (
            <Card
                withBorder={true}
                p={15}
                bg={'var(--mantine-color-read-only-input)'}
            >
                <RichTextEditor
                    editor={editor}
                    w={'100%'}
                    style={{ textAlign: 'left', border: 'none' }}
                >
                    <RichTextEditor.Content
                        className={'readonly-rte-content'}
                        mah={maxContentHeight}
                        style={{ overflowY: 'auto' }}
                    />
                </RichTextEditor>
            </Card>
        );
    }

    return (
        <RichTextEditor
            editor={editor}
            w={'100%'}
            style={{ textAlign: 'left' }}
            onKeyUp={(event) => event.stopPropagation()}
            onFocus={open}
            onBlur={close}
        >
            {(!toolBarOnFocus || focused) && (
                <RichTextEditor.Toolbar {...toolbarProps}>
                    <RichTextEditor.ControlsGroup>
                        <RichTextEditor.Bold />
                        <RichTextEditor.Italic />
                        <RichTextEditor.Underline />
                        <RichTextEditor.Strikethrough />
                        <RichTextEditor.ClearFormatting />
                        <RichTextEditor.Highlight />
                        <RichTextEditor.Code />
                    </RichTextEditor.ControlsGroup>

                    <RichTextEditor.ControlsGroup>
                        <RichTextEditor.H1 />
                        <RichTextEditor.H2 />
                        <RichTextEditor.H3 />
                        <RichTextEditor.H4 />
                    </RichTextEditor.ControlsGroup>

                    <RichTextEditor.ControlsGroup>
                        <RichTextEditor.Blockquote />
                        <RichTextEditor.Hr />
                        <RichTextEditor.BulletList />
                        <RichTextEditor.OrderedList />
                        <RichTextEditor.Subscript />
                        <RichTextEditor.Superscript />
                    </RichTextEditor.ControlsGroup>

                    <RichTextEditor.ControlsGroup>
                        <RichTextEditor.Link />
                        <RichTextEditor.Unlink />
                    </RichTextEditor.ControlsGroup>

                    <RichTextEditor.ControlsGroup>
                        <RichTextEditor.AlignLeft />
                        <RichTextEditor.AlignCenter />
                        <RichTextEditor.AlignJustify />
                        <RichTextEditor.AlignRight />
                    </RichTextEditor.ControlsGroup>

                    <RichTextEditor.ControlsGroup>
                        <RichTextEditor.ColorPicker
                            colors={[
                                '#25262b',
                                '#868e96',
                                '#fa5252',
                                '#e64980',
                                '#be4bdb',
                                '#7950f2',
                                '#4c6ef5',
                                '#228be6',
                                '#15aabf',
                                '#12b886',
                                '#40c057',
                                '#82c91e',
                                '#fab005',
                                '#fd7e14',
                            ]}
                        />
                        <RichTextEditor.UnsetColor />
                    </RichTextEditor.ControlsGroup>

                    <RichTextEditor.ControlsGroup>
                        <RichTextEditor.Undo />
                        <RichTextEditor.Redo />
                    </RichTextEditor.ControlsGroup>
                </RichTextEditor.Toolbar>
            )}

            <RichTextEditor.Content
                mah={maxContentHeight}
                style={{ overflowY: 'auto' }}
            />
        </RichTextEditor>
    );
};
